/**
 * @module public/js/admin/userPreferencesAdapter.js
 * @summary Normalise and merge per-user preference documents for the admin UI.
 *
 * Provides helpers that shape `UserPreferences` snapshots into row-friendly
 * objects and merge admin edits back into Firestore payloads without losing
 * defaults.
 */

export const DEFAULT_USER_PREFERENCES = {
  emailOptOut: false,
  callerIdPreference: 'topsail',
  callerIdSetupRequired: false,
  autoPauseOnEmailDeliveryFailureOverride: null,
  focusModeTooltips: true,
  focusModeForceSystemDialerOnMobile: true,
  focusModeEmailBrainstormEnabled: false,
  focusModeEmailRewriteEnabled: false,
  focusModeCrmNotesOpenByDefault: false,
  contactLocalCallWindowEnabled: false,
  contactLocalCallWindowMode: 'automatic',
  contactLocalCallWindowStart: '08:00',
  contactLocalCallWindowEnd: '18:00',
  activityReportVariant: 'standard',
  activityReportDays: [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ],
  progressCutoffMode: 'previous_completed_day',
  weeklyActivityReport: false,
  weeklyActivityReportDay: 'monday',
  weeklyActivityReportTime: '08:00',
  monthlyActivityReport: false,
  monthlyActivityReportDay: 1,
  monthlyActivityReportTime: '08:00',
};

export const ACTIVITY_REPORT_DAY_VALUES = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];
export const ACTIVITY_REPORT_WEEKDAY_VALUES = ACTIVITY_REPORT_DAY_VALUES.slice(
  0,
  5
);
export const ACTIVITY_REPORT_PRESET_EVERY_DAY = 'every_day';
export const ACTIVITY_REPORT_PRESET_WEEKDAYS = 'weekdays';
export const ACTIVITY_REPORT_PRESET_CUSTOM = 'custom';
export const ACTIVITY_REPORT_VARIANT_STANDARD = 'standard';
export const ACTIVITY_REPORT_VARIANT_ACTIVITY_AGAINST_GOAL =
  'activityAgainstGoal';
export const ACTIVITY_REPORT_VARIANT_ACTIVITY_AGAINST_GOAL_ONLY =
  ACTIVITY_REPORT_VARIANT_ACTIVITY_AGAINST_GOAL;
export const CONTACT_LOCAL_CALL_WINDOW_MODE_AUTOMATIC = 'automatic';
export const CONTACT_LOCAL_CALL_WINDOW_MODE_MANUAL = 'manual';
const LEGACY_ACTIVITY_REPORT_VARIANT_ACTIVITY_AGAINST_GOAL_ONLY =
  'activity_against_goal_only';

const WEEKLY_REPORT_DAY_VALUES = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];
const PROGRESS_CUTOFF_MODE_VALUES = [
  'previous_completed_day',
  'include_current_day',
];
const ACTIVITY_REPORT_DAY_VALUE_SET = new Set(ACTIVITY_REPORT_DAY_VALUES);
const ACTIVITY_REPORT_VARIANT_VALUES = new Set([
  ACTIVITY_REPORT_VARIANT_STANDARD,
  ACTIVITY_REPORT_VARIANT_ACTIVITY_AGAINST_GOAL,
]);
const CONTACT_LOCAL_CALL_WINDOW_MODE_VALUES = new Set([
  CONTACT_LOCAL_CALL_WINDOW_MODE_AUTOMATIC,
  CONTACT_LOCAL_CALL_WINDOW_MODE_MANUAL,
]);

/**
 * Normalize a per-user delivery-pause override.
 *
 * The persisted value is nullable: `null` inherits the tenant setting, while
 * booleans explicitly enable or disable automatic pauses for the user.
 *
 * @param {unknown} value - Raw preference value or User Admin select value.
 * @returns {boolean|null} Explicit override, or null to inherit the tenant.
 */
export function normalizeAutoPauseOnEmailDeliveryFailureOverride(value) {
  if (value === true || value === 'enabled') return true;
  if (value === false || value === 'disabled') return false;
  return null;
}

/**
 * Normalize contact-local call window mode values into a supported selection.
 *
 * @param {unknown} value - Raw mode value from Firestore or the UI.
 * @returns {'automatic'|'manual'} Valid mode or the default selection.
 */
export function normalizeContactLocalCallWindowMode(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (CONTACT_LOCAL_CALL_WINDOW_MODE_VALUES.has(normalized)) {
    return normalized;
  }
  return DEFAULT_USER_PREFERENCES.contactLocalCallWindowMode;
}

/**
 * Normalize call-window time values into a quarter-hour HH:MM string.
 *
 * @param {unknown} value - Raw time value from Firestore or the UI.
 * @param {string} [fallback=DEFAULT_USER_PREFERENCES.contactLocalCallWindowStart] - Fallback HH:MM string.
 * @returns {string} Quarter-hour HH:MM string or the provided fallback.
 */
export function normalizeContactLocalCallWindowTime(
  value,
  fallback = DEFAULT_USER_PREFERENCES.contactLocalCallWindowStart
) {
  const candidate = String(value || '').trim();
  const match = /^(\d{1,2}):(\d{2})$/.exec(candidate);
  if (!match) {
    return fallback;
  }

  const hourPart = Number.parseInt(match[1], 10);
  const minutePart = Number.parseInt(match[2], 10);
  if (
    !Number.isInteger(hourPart) ||
    !Number.isInteger(minutePart) ||
    hourPart < 0 ||
    hourPart > 23 ||
    minutePart < 0 ||
    minutePart > 59
  ) {
    return fallback;
  }

  const bucketedMinutes = Math.floor(minutePart / 15) * 15;
  return `${String(hourPart).padStart(2, '0')}:${String(
    bucketedMinutes
  ).padStart(2, '0')}`;
}

/**
 * Determine whether a same-day call window is ordered correctly.
 *
 * V1 rejects overnight windows, so the start time must be strictly before the
 * end time.
 *
 * @param {unknown} start - Raw or normalized start HH:MM value.
 * @param {unknown} end - Raw or normalized end HH:MM value.
 * @returns {boolean} True when the range is valid for a same-day window.
 */
export function isValidContactLocalCallWindowRange(start, end) {
  const safeStart = normalizeContactLocalCallWindowTime(
    start,
    DEFAULT_USER_PREFERENCES.contactLocalCallWindowStart
  );
  const safeEnd = normalizeContactLocalCallWindowTime(
    end,
    DEFAULT_USER_PREFERENCES.contactLocalCallWindowEnd
  );
  return safeStart < safeEnd;
}

/**
 * Normalize a raw weekday array into canonical daily report days.
 *
 * Invalid, duplicate, and out-of-order entries are removed. Missing or empty
 * values fall back to the provided selection, or all seven days by default.
 *
 * @param {unknown} value - Raw weekday array from Firestore or the UI.
 * @param {string[]} [fallback=DEFAULT_USER_PREFERENCES.activityReportDays] - Fallback weekday selection.
 * @returns {string[]} Canonical weekday list in Monday-Sunday order.
 */
export function normalizeActivityReportDays(
  value,
  fallback = DEFAULT_USER_PREFERENCES.activityReportDays
) {
  const fallbackDays =
    Array.isArray(fallback) && fallback.length > 0
      ? fallback
      : DEFAULT_USER_PREFERENCES.activityReportDays;
  if (!Array.isArray(value)) {
    return [...fallbackDays];
  }

  const selectedDays = new Set(
    value
      .map((entry) =>
        String(entry || '')
          .trim()
          .toLowerCase()
      )
      .filter((entry) => ACTIVITY_REPORT_DAY_VALUE_SET.has(entry))
  );
  if (selectedDays.size === 0) {
    return [...fallbackDays];
  }

  return ACTIVITY_REPORT_DAY_VALUES.filter((day) => selectedDays.has(day));
}

/**
 * Resolve the preset label that matches a canonical daily weekday selection.
 *
 * @param {unknown} value - Raw weekday array from Firestore or the UI.
 * @returns {'every_day'|'weekdays'|'custom'} Preset key for the current selection.
 */
export function getActivityReportDayPreset(value) {
  const normalized =
    Array.isArray(value) && value.length === 0
      ? []
      : normalizeActivityReportDays(value);
  if (
    normalized.length === ACTIVITY_REPORT_DAY_VALUES.length &&
    normalized.every((day, index) => day === ACTIVITY_REPORT_DAY_VALUES[index])
  ) {
    return ACTIVITY_REPORT_PRESET_EVERY_DAY;
  }
  if (
    normalized.length === ACTIVITY_REPORT_WEEKDAY_VALUES.length &&
    normalized.every(
      (day, index) => day === ACTIVITY_REPORT_WEEKDAY_VALUES[index]
    )
  ) {
    return ACTIVITY_REPORT_PRESET_WEEKDAYS;
  }
  return ACTIVITY_REPORT_PRESET_CUSTOM;
}

/**
 * Return the canonical weekday selection for a daily report preset.
 *
 * Custom selections preserve the provided days, including an empty array so
 * the caller can validate "enabled but no days selected" as an explicit error.
 *
 * @param {unknown} preset - UI preset key chosen by the admin.
 * @param {unknown} [customDays=[]] - Raw custom weekday selection to preserve.
 * @returns {string[]} Canonical weekday list for the selected preset.
 */
export function getActivityReportDaysForPreset(preset, customDays = []) {
  const normalizedPreset = String(preset || '')
    .trim()
    .toLowerCase();
  if (normalizedPreset === ACTIVITY_REPORT_PRESET_EVERY_DAY) {
    return [...ACTIVITY_REPORT_DAY_VALUES];
  }
  if (normalizedPreset === ACTIVITY_REPORT_PRESET_WEEKDAYS) {
    return [...ACTIVITY_REPORT_WEEKDAY_VALUES];
  }
  if (!Array.isArray(customDays) || customDays.length === 0) {
    return [];
  }
  return normalizeActivityReportDays(customDays, customDays);
}

/**
 * Normalize daily report variants into a supported report selection key.
 *
 * @param {unknown} value - Raw variant value from Firestore or the UI.
 * @returns {'standard'|'activityAgainstGoal'} Valid variant or the default selection.
 */
export function normalizeActivityReportVariant(value) {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return DEFAULT_USER_PREFERENCES.activityReportVariant;
  }
  if (
    normalized === ACTIVITY_REPORT_VARIANT_ACTIVITY_AGAINST_GOAL ||
    normalized.toLowerCase() ===
      ACTIVITY_REPORT_VARIANT_ACTIVITY_AGAINST_GOAL.toLowerCase() ||
    normalized.toLowerCase() ===
      LEGACY_ACTIVITY_REPORT_VARIANT_ACTIVITY_AGAINST_GOAL_ONLY
  ) {
    return ACTIVITY_REPORT_VARIANT_ACTIVITY_AGAINST_GOAL;
  }
  if (normalized.toLowerCase() === ACTIVITY_REPORT_VARIANT_STANDARD) {
    return ACTIVITY_REPORT_VARIANT_STANDARD;
  }
  return DEFAULT_USER_PREFERENCES.activityReportVariant;
}

/**
 * Normalize progress cutoff mode values into an allowed report-cutoff key.
 *
 * @param {unknown} value - Raw cutoff mode value from Firestore or the UI.
 * @returns {'previous_completed_day'|'include_current_day'} Valid cutoff mode or the default mode.
 */
export function normalizeProgressCutoffMode(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (PROGRESS_CUTOFF_MODE_VALUES.includes(normalized)) {
    return normalized;
  }
  return DEFAULT_USER_PREFERENCES.progressCutoffMode;
}

/**
 * Normalize weekly report weekday values into a lowercase full weekday string.
 *
 * @param {unknown} value - Raw weekday value from Firestore or the UI.
 * @returns {string} Valid weekday name or the default weekly report day.
 */
export function normalizeWeeklyActivityReportDay(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (WEEKLY_REPORT_DAY_VALUES.includes(normalized)) {
    return normalized;
  }
  return DEFAULT_USER_PREFERENCES.weeklyActivityReportDay;
}

/**
 * Normalize weekly report time values into a quarter-hour HH:MM string.
 *
 * @param {unknown} value - Raw time value from Firestore or the UI.
 * @returns {string} Quarter-hour HH:MM string or the default weekly report time.
 */
export function normalizeWeeklyActivityReportTime(value) {
  const [hourPart, minutePart] = String(value || '')
    .split(':')
    .map((part) => Number.parseInt(part, 10));
  if (Number.isNaN(hourPart) || Number.isNaN(minutePart)) {
    return DEFAULT_USER_PREFERENCES.weeklyActivityReportTime;
  }
  const bucketedMinutes = Math.floor(minutePart / 15) * 15;
  return `${String(hourPart).padStart(2, '0')}:${String(
    bucketedMinutes
  ).padStart(2, '0')}`;
}

/**
 * Normalize monthly report day values into an integer between 1 and 31.
 *
 * @param {unknown} value - Raw month-day value from Firestore or the UI.
 * @returns {number} Calendar day value or the default monthly report day.
 */
export function normalizeMonthlyActivityReportDay(value) {
  const normalized = Number.parseInt(value, 10);
  if (Number.isInteger(normalized) && normalized >= 1 && normalized <= 31) {
    return normalized;
  }
  return DEFAULT_USER_PREFERENCES.monthlyActivityReportDay;
}

/**
 * Normalize monthly report time values into a quarter-hour HH:MM string.
 *
 * @param {unknown} value - Raw time value from Firestore or the UI.
 * @returns {string} Quarter-hour HH:MM string or the default monthly report time.
 */
export function normalizeMonthlyActivityReportTime(value) {
  const [hourPart, minutePart] = String(value || '')
    .split(':')
    .map((part) => Number.parseInt(part, 10));
  if (Number.isNaN(hourPart) || Number.isNaN(minutePart)) {
    return DEFAULT_USER_PREFERENCES.monthlyActivityReportTime;
  }
  const bucketedMinutes = Math.floor(minutePart / 15) * 15;
  return `${String(hourPart).padStart(2, '0')}:${String(
    bucketedMinutes
  ).padStart(2, '0')}`;
}

/**
 * Normalise a raw `UserPreferences` snapshot into admin-friendly flags.
 *
 * @param {Record<string, unknown>|null|undefined} data - Firestore payload.
 * @returns {{emailOptOut: boolean, callerIdPreference: 'topsail'|'own', callerIdSetupRequired: boolean, focusModeTooltips: boolean, focusModeForceSystemDialerOnMobile: boolean, focusModeEmailBrainstormEnabled: boolean, focusModeEmailRewriteEnabled: boolean, focusModeCrmNotesOpenByDefault: boolean, activityReportVariant: 'standard'|'activityAgainstGoal', activityReportDays: string[], progressCutoffMode: 'previous_completed_day'|'include_current_day', weeklyActivityReport: boolean, weeklyActivityReportDay: string, weeklyActivityReportTime: string, monthlyActivityReport: boolean, monthlyActivityReportDay: number, monthlyActivityReportTime: string}} Parsed prefs.
 */
export function normalizeUserPreferences(data = {}) {
  return {
    emailOptOut: data?.emailOptOut === true,
    callerIdPreference: data?.callerIdPreference === 'own' ? 'own' : 'topsail',
    callerIdSetupRequired: data?.callerIdSetupRequired === true,
    autoPauseOnEmailDeliveryFailureOverride:
      normalizeAutoPauseOnEmailDeliveryFailureOverride(
        data?.autoPauseOnEmailDeliveryFailureOverride
      ),
    focusModeTooltips: data?.focusModeTooltips !== false,
    focusModeForceSystemDialerOnMobile:
      data?.focusModeForceSystemDialerOnMobile !== false,
    focusModeEmailBrainstormEnabled:
      data?.focusModeEmailBrainstormEnabled === true,
    focusModeEmailRewriteEnabled: data?.focusModeEmailRewriteEnabled === true,
    focusModeCrmNotesOpenByDefault:
      data?.focusModeCrmNotesOpenByDefault === true,
    contactLocalCallWindowEnabled: data?.contactLocalCallWindowEnabled === true,
    contactLocalCallWindowMode: normalizeContactLocalCallWindowMode(
      data?.contactLocalCallWindowMode
    ),
    contactLocalCallWindowStart: normalizeContactLocalCallWindowTime(
      data?.contactLocalCallWindowStart,
      DEFAULT_USER_PREFERENCES.contactLocalCallWindowStart
    ),
    contactLocalCallWindowEnd: normalizeContactLocalCallWindowTime(
      data?.contactLocalCallWindowEnd,
      DEFAULT_USER_PREFERENCES.contactLocalCallWindowEnd
    ),
    activityReportVariant: normalizeActivityReportVariant(
      data?.activityReportVariant
    ),
    activityReportDays: normalizeActivityReportDays(data?.activityReportDays),
    progressCutoffMode: normalizeProgressCutoffMode(data?.progressCutoffMode),
    weeklyActivityReport: data?.weeklyActivityReport === true,
    weeklyActivityReportDay: normalizeWeeklyActivityReportDay(
      data?.weeklyActivityReportDay
    ),
    weeklyActivityReportTime: normalizeWeeklyActivityReportTime(
      data?.weeklyActivityReportTime
    ),
    monthlyActivityReport: data?.monthlyActivityReport === true,
    monthlyActivityReportDay: normalizeMonthlyActivityReportDay(
      data?.monthlyActivityReportDay
    ),
    monthlyActivityReportTime: normalizeMonthlyActivityReportTime(
      data?.monthlyActivityReportTime
    ),
  };
}

/**
 * Merge existing preferences with updates while enforcing defaults.
 *
 * @param {Record<string, unknown>|null|undefined} existing - Stored prefs.
 * @param {Record<string, unknown>|null|undefined} updates - Incoming changes.
 * @param {string} [tenantId] - Tenant ID to stamp when missing.
 * @returns {{emailOptOut: boolean, callerIdPreference: 'topsail'|'own', callerIdSetupRequired: boolean, focusModeTooltips: boolean, focusModeForceSystemDialerOnMobile: boolean, focusModeEmailBrainstormEnabled: boolean, focusModeEmailRewriteEnabled: boolean, focusModeCrmNotesOpenByDefault: boolean, activityReportVariant: 'standard'|'activityAgainstGoal', activityReportDays: string[], progressCutoffMode: 'previous_completed_day'|'include_current_day', weeklyActivityReport: boolean, weeklyActivityReportDay: string, weeklyActivityReportTime: string, monthlyActivityReport: boolean, monthlyActivityReportDay: number, monthlyActivityReportTime: string, tenantId?: string}} Merged prefs.
 */
export function mergeUserPreferences(existing = {}, updates = {}, tenantId) {
  const normalizedExisting = normalizeUserPreferences(existing);
  const resolvedContactLocalCallWindowStart =
    updates?.contactLocalCallWindowStart !== undefined
      ? normalizeContactLocalCallWindowTime(
          updates.contactLocalCallWindowStart,
          normalizedExisting.contactLocalCallWindowStart
        )
      : normalizedExisting.contactLocalCallWindowStart;
  const resolvedContactLocalCallWindowEnd =
    updates?.contactLocalCallWindowEnd !== undefined
      ? normalizeContactLocalCallWindowTime(
          updates.contactLocalCallWindowEnd,
          normalizedExisting.contactLocalCallWindowEnd
        )
      : normalizedExisting.contactLocalCallWindowEnd;
  const hasContactLocalCallWindowRangeUpdate =
    updates?.contactLocalCallWindowStart !== undefined ||
    updates?.contactLocalCallWindowEnd !== undefined;
  const contactLocalCallWindowStart =
    hasContactLocalCallWindowRangeUpdate &&
    !isValidContactLocalCallWindowRange(
      resolvedContactLocalCallWindowStart,
      resolvedContactLocalCallWindowEnd
    )
      ? normalizedExisting.contactLocalCallWindowStart
      : resolvedContactLocalCallWindowStart;
  const contactLocalCallWindowEnd =
    hasContactLocalCallWindowRangeUpdate &&
    !isValidContactLocalCallWindowRange(
      resolvedContactLocalCallWindowStart,
      resolvedContactLocalCallWindowEnd
    )
      ? normalizedExisting.contactLocalCallWindowEnd
      : resolvedContactLocalCallWindowEnd;
  const merged = {
    ...(existing || {}),
    emailOptOut:
      updates?.emailOptOut !== undefined
        ? updates.emailOptOut === true
        : normalizedExisting.emailOptOut,
    callerIdPreference:
      updates?.callerIdPreference !== undefined
        ? updates.callerIdPreference === 'own'
          ? 'own'
          : 'topsail'
        : normalizedExisting.callerIdPreference,
    callerIdSetupRequired:
      updates?.callerIdSetupRequired !== undefined
        ? updates.callerIdSetupRequired === true
        : normalizedExisting.callerIdSetupRequired,
    autoPauseOnEmailDeliveryFailureOverride:
      updates?.autoPauseOnEmailDeliveryFailureOverride !== undefined
        ? normalizeAutoPauseOnEmailDeliveryFailureOverride(
            updates.autoPauseOnEmailDeliveryFailureOverride
          )
        : normalizedExisting.autoPauseOnEmailDeliveryFailureOverride,
    focusModeTooltips:
      updates?.focusModeTooltips !== undefined
        ? updates.focusModeTooltips !== false
        : normalizedExisting.focusModeTooltips,
    focusModeForceSystemDialerOnMobile:
      updates?.focusModeForceSystemDialerOnMobile !== undefined
        ? updates.focusModeForceSystemDialerOnMobile !== false
        : normalizedExisting.focusModeForceSystemDialerOnMobile,
    focusModeEmailBrainstormEnabled:
      updates?.focusModeEmailBrainstormEnabled !== undefined
        ? updates.focusModeEmailBrainstormEnabled === true
        : normalizedExisting.focusModeEmailBrainstormEnabled,
    focusModeEmailRewriteEnabled:
      updates?.focusModeEmailRewriteEnabled !== undefined
        ? updates.focusModeEmailRewriteEnabled === true
        : normalizedExisting.focusModeEmailRewriteEnabled,
    focusModeCrmNotesOpenByDefault:
      updates?.focusModeCrmNotesOpenByDefault !== undefined
        ? updates.focusModeCrmNotesOpenByDefault === true
        : normalizedExisting.focusModeCrmNotesOpenByDefault,
    contactLocalCallWindowEnabled:
      updates?.contactLocalCallWindowEnabled !== undefined
        ? updates.contactLocalCallWindowEnabled === true
        : normalizedExisting.contactLocalCallWindowEnabled,
    contactLocalCallWindowMode:
      updates?.contactLocalCallWindowMode !== undefined
        ? normalizeContactLocalCallWindowMode(
            updates.contactLocalCallWindowMode
          )
        : normalizedExisting.contactLocalCallWindowMode,
    contactLocalCallWindowStart,
    contactLocalCallWindowEnd,
    activityReportVariant:
      updates?.activityReportVariant !== undefined
        ? normalizeActivityReportVariant(updates.activityReportVariant)
        : normalizedExisting.activityReportVariant,
    activityReportDays:
      updates?.activityReportDays !== undefined
        ? normalizeActivityReportDays(updates.activityReportDays)
        : normalizedExisting.activityReportDays,
    progressCutoffMode:
      updates?.progressCutoffMode !== undefined
        ? normalizeProgressCutoffMode(updates.progressCutoffMode)
        : normalizedExisting.progressCutoffMode,
    weeklyActivityReport:
      updates?.weeklyActivityReport !== undefined
        ? updates.weeklyActivityReport === true
        : normalizedExisting.weeklyActivityReport,
    weeklyActivityReportDay:
      updates?.weeklyActivityReportDay !== undefined
        ? normalizeWeeklyActivityReportDay(updates.weeklyActivityReportDay)
        : normalizedExisting.weeklyActivityReportDay,
    weeklyActivityReportTime:
      updates?.weeklyActivityReportTime !== undefined
        ? normalizeWeeklyActivityReportTime(updates.weeklyActivityReportTime)
        : normalizedExisting.weeklyActivityReportTime,
    monthlyActivityReport:
      updates?.monthlyActivityReport !== undefined
        ? updates.monthlyActivityReport === true
        : normalizedExisting.monthlyActivityReport,
    monthlyActivityReportDay:
      updates?.monthlyActivityReportDay !== undefined
        ? normalizeMonthlyActivityReportDay(updates.monthlyActivityReportDay)
        : normalizedExisting.monthlyActivityReportDay,
    monthlyActivityReportTime:
      updates?.monthlyActivityReportTime !== undefined
        ? normalizeMonthlyActivityReportTime(updates.monthlyActivityReportTime)
        : normalizedExisting.monthlyActivityReportTime,
  };
  if (tenantId && !merged.tenantId) {
    merged.tenantId = tenantId;
  }
  if (!Object.prototype.hasOwnProperty.call(merged, 'focusModeTooltips')) {
    merged.focusModeTooltips = DEFAULT_USER_PREFERENCES.focusModeTooltips;
  }
  if (
    !Object.prototype.hasOwnProperty.call(
      merged,
      'focusModeForceSystemDialerOnMobile'
    )
  ) {
    merged.focusModeForceSystemDialerOnMobile =
      DEFAULT_USER_PREFERENCES.focusModeForceSystemDialerOnMobile;
  }
  if (
    !Object.prototype.hasOwnProperty.call(
      merged,
      'focusModeEmailBrainstormEnabled'
    )
  ) {
    merged.focusModeEmailBrainstormEnabled =
      DEFAULT_USER_PREFERENCES.focusModeEmailBrainstormEnabled;
  }
  if (
    !Object.prototype.hasOwnProperty.call(
      merged,
      'focusModeEmailRewriteEnabled'
    )
  ) {
    merged.focusModeEmailRewriteEnabled =
      DEFAULT_USER_PREFERENCES.focusModeEmailRewriteEnabled;
  }
  if (
    !Object.prototype.hasOwnProperty.call(
      merged,
      'contactLocalCallWindowEnabled'
    )
  ) {
    merged.contactLocalCallWindowEnabled =
      DEFAULT_USER_PREFERENCES.contactLocalCallWindowEnabled;
  }
  if (
    !Object.prototype.hasOwnProperty.call(merged, 'contactLocalCallWindowMode')
  ) {
    merged.contactLocalCallWindowMode =
      DEFAULT_USER_PREFERENCES.contactLocalCallWindowMode;
  }
  if (
    !Object.prototype.hasOwnProperty.call(merged, 'contactLocalCallWindowStart')
  ) {
    merged.contactLocalCallWindowStart =
      DEFAULT_USER_PREFERENCES.contactLocalCallWindowStart;
  }
  if (
    !Object.prototype.hasOwnProperty.call(merged, 'contactLocalCallWindowEnd')
  ) {
    merged.contactLocalCallWindowEnd =
      DEFAULT_USER_PREFERENCES.contactLocalCallWindowEnd;
  }
  if (!Object.prototype.hasOwnProperty.call(merged, 'emailOptOut')) {
    merged.emailOptOut = DEFAULT_USER_PREFERENCES.emailOptOut;
  }
  if (
    !Object.prototype.hasOwnProperty.call(
      merged,
      'autoPauseOnEmailDeliveryFailureOverride'
    )
  ) {
    merged.autoPauseOnEmailDeliveryFailureOverride =
      DEFAULT_USER_PREFERENCES.autoPauseOnEmailDeliveryFailureOverride;
  }
  if (!Object.prototype.hasOwnProperty.call(merged, 'activityReportDays')) {
    merged.activityReportDays = DEFAULT_USER_PREFERENCES.activityReportDays;
  }
  if (!Object.prototype.hasOwnProperty.call(merged, 'activityReportVariant')) {
    merged.activityReportVariant =
      DEFAULT_USER_PREFERENCES.activityReportVariant;
  }
  if (!Object.prototype.hasOwnProperty.call(merged, 'progressCutoffMode')) {
    merged.progressCutoffMode = DEFAULT_USER_PREFERENCES.progressCutoffMode;
  }
  if (!Object.prototype.hasOwnProperty.call(merged, 'weeklyActivityReport')) {
    merged.weeklyActivityReport = DEFAULT_USER_PREFERENCES.weeklyActivityReport;
  }
  if (
    !Object.prototype.hasOwnProperty.call(merged, 'weeklyActivityReportDay')
  ) {
    merged.weeklyActivityReportDay =
      DEFAULT_USER_PREFERENCES.weeklyActivityReportDay;
  }
  if (
    !Object.prototype.hasOwnProperty.call(merged, 'weeklyActivityReportTime')
  ) {
    merged.weeklyActivityReportTime =
      DEFAULT_USER_PREFERENCES.weeklyActivityReportTime;
  }
  if (!Object.prototype.hasOwnProperty.call(merged, 'monthlyActivityReport')) {
    merged.monthlyActivityReport =
      DEFAULT_USER_PREFERENCES.monthlyActivityReport;
  }
  if (
    !Object.prototype.hasOwnProperty.call(merged, 'monthlyActivityReportDay')
  ) {
    merged.monthlyActivityReportDay =
      DEFAULT_USER_PREFERENCES.monthlyActivityReportDay;
  }
  if (
    !Object.prototype.hasOwnProperty.call(merged, 'monthlyActivityReportTime')
  ) {
    merged.monthlyActivityReportTime =
      DEFAULT_USER_PREFERENCES.monthlyActivityReportTime;
  }
  return merged;
}
